import { ChangeEvent } from 'react';

import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreFormGroup, CoreTypography } from '@youscience/core';

import SectionAccordion from '@components/SectionAccordion';

import ContinueButton from '@components/ContinueButton';

import { FormState } from '@constants/commonApp/types';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';
import { OTHER_UPDATES_LABELS, SUMMARY_UPDATES_LABELS } from '@constants/recommenderWorkspace';
import { sxStyles } from './Summary.styles';

export interface SummaryFields {
  classRank?: boolean;
  gpa?: boolean;
  transcripts?: boolean;
  enrolledCourses?: boolean;
  evaluation?: boolean;
  otherCorrection?: boolean;
}

interface SummaryProps {
  summary?: SummaryFields;
  onFormChange: (formState: FormState<SummaryFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  showError?: boolean;
}

export const Summary = ({
  summary = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
  showError,
}: SummaryProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<SummaryFields, undefined>(
    summary,
    undefined,
    isSaved,
    onFormChange,
  );

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormStateWithErrors({ [event.target.name]: checked });
  };

  return (
    <SectionAccordion
      title="Summary"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <CoreBox sx={sxStyles.summaryTitlesWrap}>
          <CoreTypography variant="subtitle2" component="h4">
            Use this form if you need to update submitted reports or add new information for the student. This form is
            not required.
          </CoreTypography>

          <CoreTypography sx={sxStyles.infoText} variant="body2">
            What updates are you making?
          </CoreTypography>
        </CoreBox>

        <CoreFormGroup data-testid="summaryUpdates" sx={sxStyles.checkboxGroup}>
          {Object.entries(SUMMARY_UPDATES_LABELS).map(([key, label]) => (
            <CoreFormControlLabel
              key={key}
              control={
                <CoreCheckbox
                  data-testid={key}
                  name={key}
                  checked={formState.data[key as keyof SummaryFields] ?? false}
                  onChange={handleCheckBoxChange}
                />
              }
              label={label}
            />
          ))}

          {Object.entries(OTHER_UPDATES_LABELS).map(([key, label]) => (
            <CoreFormControlLabel
              key={key}
              control={
                <CoreCheckbox
                  data-testid={key}
                  name={key}
                  checked={formState.data[key as keyof SummaryFields] ?? false}
                  onChange={handleCheckBoxChange}
                />
              }
              label={label}
            />
          ))}
        </CoreFormGroup>

        {onContinue && <ContinueButton onContinue={onContinue} />}
      </CoreBox>
    </SectionAccordion>
  );
};
